import React from 'react';
import { Lock, Shield, CreditCard, ArrowRight } from 'lucide-react';
import Icon from '../../../components/AppIcon';

const ServiceSelectionDashboard = ({ activeService, onServiceSelect }) => {
  const services = [
    {
      id: 'password-reset',
      title: 'Password Reset',
      description: 'Reset your account password securely via email verification',
      icon: Lock,
      color: 'emerald',
      features: [
        'Email verification',
        'Secure password requirements',
        'Instant password update'
      ]
    },
    {
      id: 'account-recovery',
      title: 'Account Recovery',
      description: 'Recover access to locked or suspended accounts',
      icon: Shield,
      color: 'blue',
      features: [
        'Multi-factor verification',
        'Identity confirmation',
        'Automated unlock process'
      ]
    },
    {
      id: 'payment-verification',
      title: 'Payment Verification',
      description: 'Verify external bank transfer payment status',
      icon: CreditCard,
      color: 'purple',
      features: [
        'Transaction matching',
        'Real-time status check',
        'Payment history access'
      ]
    }
  ];

  const getColorClasses = (color, isActive) => {
    const colors = {
      emerald: {
        border: isActive ? 'border-emerald-500' : 'border-emerald-200 dark:border-emerald-800',
        bg: isActive ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-white dark:bg-slate-800',
        icon: 'text-emerald-600 dark:text-emerald-400',
        iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
        hover: 'hover:border-emerald-300 dark:hover:border-emerald-700'
      },
      blue: {
        border: isActive ? 'border-blue-500' : 'border-blue-200 dark:border-blue-800',
        bg: isActive ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-slate-800',
        icon: 'text-blue-600 dark:text-blue-400',
        iconBg: 'bg-blue-100 dark:bg-blue-900/30',
        hover: 'hover:border-blue-300 dark:hover:border-blue-700'
      },
      purple: {
        border: isActive ? 'border-purple-500' : 'border-purple-200 dark:border-purple-800',
        bg: isActive ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-white dark:bg-slate-800',
        icon: 'text-purple-600 dark:text-purple-400',
        iconBg: 'bg-purple-100 dark:bg-purple-900/30',
        hover: 'hover:border-purple-300 dark:hover:border-purple-700'
      }
    };
    return colors?.[color];
  };

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">Self-Service Options</h3>
        <p className="text-sm text-muted-foreground">
          Select a service below to manage your account without admin assistance
        </p>
      </div>

      {services?.map((service) => {
        const isActive = activeService === service?.id;
        const colors = getColorClasses(service?.color, isActive);
        const IconComponent = service?.icon;

        return (
          <button
            key={service?.id}
            onClick={() => onServiceSelect(service?.id)}
            className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
              colors?.border
            } ${colors?.bg} ${!isActive && colors?.hover}`}
          >
            <div className="flex items-start gap-4">
              <div className={`${colors?.iconBg} p-3 rounded-lg`}>
                <IconComponent size={24} className={colors?.icon} />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-foreground">{service?.title}</h4>
                  {isActive && (
                    <span className={`text-xs px-2 py-1 rounded ${colors?.iconBg} ${colors?.icon} font-medium`}>
                      Active
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {service?.description}
                </p>
                <ul className="space-y-1">
                  {service?.features?.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Check" size={14} className={colors?.icon} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <ArrowRight size={20} className={isActive ? colors?.icon : 'text-muted-foreground'} />
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ServiceSelectionDashboard;